import React from 'react';

/**
 * Route-level code splitting that survives a deploy.
 *
 * Every screen is its own hashed chunk (Patients.<hash>.js). A tab opened before
 * a deploy still runs the old app bundle, which asks for the old chunk names —
 * and those files are gone from the new image. The import rejects, React.lazy
 * rethrows, and with no error boundary the navigation simply dies:
 *
 *   Uncaught TypeError: error loading dynamically imported module:
 *   https://beta1.clinic.parsinabot.ir/Patients.45ac155e.js
 *
 * A reload fetches the current index.html, which names the current chunks, so
 * that is what happens — ONCE. A flag in sessionStorage stops a genuinely broken
 * build from reloading forever: the second failure is thrown as before. A load
 * that succeeds clears the flag, so the next deploy gets its reload too.
 */

export const CHUNK_RELOAD_KEY = 'parsina:chunk-reload';

const CHUNK_ERROR = new RegExp([
  'error loading dynamically imported module',     // Firefox
  'Failed to fetch dynamically imported module',   // Chromium
  'Importing a module script failed',              // Safari
  'disallowed MIME type',
  'Loading chunk',
  'ChunkLoadError',
  'Could not load',
].join('|'), 'i');

export function isChunkLoadError(err) {
  if (!err) return false;
  return err.name === 'ChunkLoadError' || CHUNK_ERROR.test(String(err.message || err));
}

function sessionStore() {
  try { return window.sessionStorage; } catch { return null; }
}

export function importWithReload(importer, {
  storage = sessionStore(),
  reload = () => window.location.reload(),
} = {}) {
  return importer().then(
    (mod) => {
      try { storage?.removeItem(CHUNK_RELOAD_KEY); } catch { /* ignore */ }
      return mod;
    },
    (err) => {
      if (!isChunkLoadError(err)) throw err;
      let alreadyReloaded = false;
      try { alreadyReloaded = storage?.getItem(CHUNK_RELOAD_KEY) === '1'; } catch { /* ignore */ }
      if (alreadyReloaded || !storage) throw err;
      try { storage.setItem(CHUNK_RELOAD_KEY, '1'); } catch { throw err; }
      reload();
      // Never settles: the page is being replaced, and rendering anything in the
      // meantime would only flash an error the reload is about to fix.
      return new Promise(() => {});
    },
  );
}

export const lazyWithReload = (importer, options) =>
  React.lazy(() => importWithReload(importer, options));
